import type { NextApiRequest, NextApiResponse } from 'next';
import { ASSISTANT_TOOLS, executeToolCall } from '@/lib/ai-tools';

interface AsesorChatRequest {
  asesorId: string;
  conversacionId: string;
  mensaje: string;
  historial?: { rol: string; contenido: string }[];
}

interface ToolCallInfo {
  name: string;
  args: Record<string, unknown>;
  result: { success: boolean; data?: unknown; error?: string };
}

interface AsesorChatResponse {
  success: boolean;
  respuesta?: string;
  fuentes?: string[];
  toolCalls?: ToolCallInfo[];
  error?: string;
}

const MAX_TOOL_ITERATIONS = 5;

/**
 * Asesor Assistant Chat API with OpenAI + Function Calling
 * The AI can use tools to query/mutate real data in the system
 */
export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse<AsesorChatResponse>
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  try {
    const { asesorId, conversacionId, mensaje, historial } = req.body as AsesorChatRequest;

    if (!asesorId || !conversacionId || !mensaje) {
      return res.status(400).json({ success: false, error: 'Missing required fields' });
    }

    const openaiApiKey = process.env.OPENAI_API_KEY;

    if (!openaiApiKey) {
      const respuestaSimple = generarRespuestaSimple(mensaje);
      return res.status(200).json({
        success: true,
        respuesta: respuestaSimple,
        fuentes: ['Plan General Contable', 'Normativa fiscal vigente'],
      });
    }

    // Build conversation context for OpenAI
    const messages: Array<{ role: string; content?: string | null; tool_calls?: unknown[]; tool_call_id?: string }> = [
      {
        role: 'system',
        content: `Eres E2, un asistente inteligente especializado en contabilidad, fiscalidad y gestión empresarial para asesores fiscales en España.

CAPACIDADES CON HERRAMIENTAS:
Tienes acceso a herramientas del sistema que te permiten realizar acciones REALES:
- Listar y buscar clientes asignados
- Listar, buscar y CREAR facturas
- Buscar documentos subidos y procesarlos con OCR
- Consultar resúmenes financieros de clientes
- Listar y crear incidencias

REGLAS CRÍTICAS:
- SIEMPRE responde en español
- Cuando el usuario pida datos concretos (clientes, facturas, documentos), USA las herramientas — NO inventes datos
- Para crear facturas o incidencias, confirma los datos con el usuario antes de ejecutar la herramienta
- Cuando uses una herramienta y obtengas resultados, preséntalos de forma clara y organizada con markdown
- Si una herramienta falla, explica el error al usuario y sugiere alternativas
- Usa lenguaje técnico pero claro
- Estructura tus respuestas con headers, bullets o tablas cuando sea apropiado
- Cita la normativa aplicable cuando sea relevante
- Si no estás seguro de algo, indícalo claramente`,
      },
    ];

    // Add conversation history
    if (historial && historial.length > 0) {
      historial.slice(-8).forEach((msg) => {
        messages.push({
          role: msg.rol === 'usuario' ? 'user' : 'assistant',
          content: msg.contenido,
        });
      });
    }

    // Add current question
    messages.push({ role: 'user', content: mensaje });

    // Tool execution context
    const toolContext = { userId: asesorId, userRole: 'asesor' };
    const allToolCalls: ToolCallInfo[] = [];

    // ─── Function Calling Loop ───
    let finalResponse = '';
    for (let iteration = 0; iteration < MAX_TOOL_ITERATIONS; iteration++) {
      const openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${openaiApiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages,
          tools: ASSISTANT_TOOLS,
          tool_choice: 'auto',
          temperature: 0.7,
          max_tokens: 3000,
        }),
      });

      if (!openaiResponse.ok) {
        const error = await openaiResponse.text();
        console.error('OpenAI API error:', error);
        throw new Error('Error al comunicarse con OpenAI');
      }

      const openaiData = await openaiResponse.json();
      const choice = openaiData.choices[0];
      const assistantMessage = choice.message;

      // If the model wants to call tools
      if (choice.finish_reason === 'tool_calls' && assistantMessage.tool_calls) {
        // Add the assistant message with tool_calls to history
        messages.push({
          role: 'assistant',
          content: assistantMessage.content || null,
          tool_calls: assistantMessage.tool_calls,
        });

        // Execute each tool call
        for (const toolCall of assistantMessage.tool_calls) {
          const fnName = toolCall.function.name;
          let fnArgs: Record<string, unknown> = {};
          try {
            fnArgs = JSON.parse(toolCall.function.arguments);
          } catch {
            fnArgs = {};
          }

          console.log(`[AI Tool] Executing: ${fnName}`, fnArgs);
          const result = await executeToolCall(fnName, fnArgs, toolContext);

          allToolCalls.push({ name: fnName, args: fnArgs, result });

          // Add tool result to message history
          messages.push({
            role: 'tool',
            tool_call_id: toolCall.id,
            content: JSON.stringify(result),
          });
        }

        // Continue the loop — OpenAI will process the tool results
        continue;
      }

      // Model returned a final text response
      finalResponse = assistantMessage.content || '';
      break;
    }

    // Determine fuentes based on tools used
    const fuentes = ['Plan General Contable', 'Normativa fiscal vigente'];
    if (allToolCalls.length > 0) {
      fuentes.unshift('Datos del sistema Externaliza2');
    }

    return res.status(200).json({
      success: true,
      respuesta: finalResponse,
      fuentes,
      toolCalls: allToolCalls.length > 0 ? allToolCalls : undefined,
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Error interno del servidor';
    console.error('Asesor assistant chat error:', message);
    return res.status(500).json({
      success: false,
      error: message,
    });
  }
}

/**
 * Simple fallback response when OpenAI is not available
 */
function generarRespuestaSimple(pregunta: string): string {
  const preguntaLower = pregunta.toLowerCase();

  if (preguntaLower.includes('intracomunitaria')) {
    return 'Para contabilizar una factura intracomunitaria:\n\n1. En el debe: Cuenta 600 (Compras) por el importe\n2. En el debe: Cuenta 472 (IVA soportado) por el IVA\n3. En el haber: Cuenta 477 (IVA repercutido) por el mismo IVA\n4. En el haber: Cuenta 400 (Proveedores) por el total\n\nRecuerda que el IVA se autoliquida.';
  }
  if (preguntaLower.includes('iva reducido') || preguntaLower.includes('superreducido')) {
    return 'IVA General: 21% - Mayoría de bienes y servicios\nIVA Reducido: 10% - Alimentos básicos, transporte, hostelería\nIVA Superreducido: 4% - Productos de primera necesidad (pan, leche, libros, medicamentos)\n\nCada tipo tiene aplicaciones específicas según la normativa vigente.';
  }
  if (preguntaLower.includes('plazo') || preguntaLower.includes('fecha')) {
    return 'Plazos fiscales principales:\n\n• IVA mensual: Día 20\n• IRPF trimestral: Día 20\n• Retenciones (Modelo 111): Día 20\n• IVA trimestral: Día 20 (siguiente mes)\n\nTe recomiendo revisar el calendario fiscal completo en el módulo correspondiente.';
  }

  return 'Estoy aquí para ayudarte con consultas contables, fiscales y normativas. Puedo asistirte con contabilización, interpretación de normativa, plazos fiscales y gestión de tus clientes. ¿En qué puedo ayudarte específicamente?';
}
